import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { getChatbotBaseUrl, loadChatbotEmbedDocument } from '../../services/apiClient';

/** Scales embedded chatbot UI (iframe) — content is cross-origin so we scale the frame. */
function getChatbotUiScale(viewportWidth) {
  if (viewportWidth < 480) return 0.68;
  if (viewportWidth < 768) return 0.72;
  if (viewportWidth < 1024) return 0.76;
  if (viewportWidth < 1280) return 0.8;
  return 0.82;
}

const LandingChatbot = ({ email }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [chatbotSrcDoc, setChatbotSrcDoc] = useState('');
  const [chatbotFallbackSrc, setChatbotFallbackSrc] = useState('');
  const [isLoadingChatbot, setIsLoadingChatbot] = useState(false);
  const [chatbotLoadError, setChatbotLoadError] = useState('');
  const loadRequestId = useRef(0);
  const [uiScale, setUiScale] = useState(() =>
    typeof window !== 'undefined' ? getChatbotUiScale(window.innerWidth) : 0.8
  );

  useEffect(() => {
    const onResize = () => setUiScale(getChatbotUiScale(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isOpen || isMinimized) return;

    const userId = sessionStorage.getItem('userId');
    const tenantId = sessionStorage.getItem('tenantId');
    const roleId = sessionStorage.getItem('roleId');
    const base = getChatbotBaseUrl();

    if (!email || !userId || !tenantId || !roleId) {
      setChatbotSrcDoc('');
      setChatbotFallbackSrc(`${base}/chatbot?embed=compact`);
      setChatbotLoadError('');
      return;
    }

    const requestId = ++loadRequestId.current;
    setIsLoadingChatbot(true);
    setChatbotLoadError('');
    setChatbotFallbackSrc('');

    loadChatbotEmbedDocument({ email, userId, tenantId, roleId, embed: 'compact' })
      .then(({ srcDoc, iframeSrc }) => {
        if (loadRequestId.current !== requestId) return;
        setChatbotSrcDoc(srcDoc || '');
        setChatbotFallbackSrc(iframeSrc || '');
        setChatbotLoadError('');
      })
      .catch((err) => {
        if (loadRequestId.current !== requestId) return;
        console.error('Failed to load chatbot:', err);
        setChatbotSrcDoc('');
        setChatbotFallbackSrc(
          `${base}/chatbot/${email}/${userId}/${tenantId}/${roleId}?embed=compact`
        );
        setChatbotLoadError('Unable to load chatbot. Showing fallback view.');
      })
      .finally(() => {
        if (loadRequestId.current === requestId) {
          setIsLoadingChatbot(false);
        }
      });
  }, [isOpen, isMinimized, email]);

  const iframeWrapperStyle = useMemo(() => {
    const sizePct = `${100 / uiScale}%`;
    return {
      width: sizePct,
      height: sizePct,
      transform: `scale(${uiScale})`,
      transformOrigin: 'top left',
    };
  }, [uiScale]);

  const handleToggle = () => {
    if (isOpen) {
      setIsMinimized(true);
      setTimeout(() => setIsOpen(false), 300);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleExpand = () => {
    setIsMinimized(false);
  };

  return (
    <>
      {!isOpen && (
        <button
          onClick={handleToggle}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 h-14 w-14 rounded-full bg-[#006600] text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center"
          aria-label="Open chatbot"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {isOpen && (
        <div
          className={`fixed bg-white shadow-2xl z-50 transition-all duration-300 flex flex-col border border-[#E6F0F2] overflow-hidden bottom-4 right-4 sm:bottom-6 sm:right-6 rounded-xl ${isMinimized
              ? 'w-[clamp(18rem,calc(100vw-2rem),24rem)] h-24'
              : 'w-[clamp(16rem,min(58rem,calc(100vw-2rem)),58rem)] h-[clamp(15rem,min(35rem,calc(100vh-8rem)),35rem)]'
            }`}
        >
          <div className="flex items-center justify-between px-3 py-2 bg-[#0AAE94] rounded-t-xl flex-shrink-0">
            <h3 className="text-white font-medium text-sm">Chatbot</h3>
            <div className="flex items-center gap-2">
              {isMinimized && (
                <button
                  onClick={handleExpand}
                  className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                  aria-label="Expand"
                >
                  <MessageCircle className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={handleToggle}
                className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <div className="flex-1 min-h-0 overflow-hidden bg-white relative">
              {isLoadingChatbot && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 z-10 bg-white">
                  Loading chatbot…
                </div>
              )}
              {chatbotLoadError && !isLoadingChatbot && (
                <p className="absolute top-2 left-2 right-2 text-xs text-amber-700 z-10 bg-amber-50 px-2 py-1 rounded">
                  {chatbotLoadError}
                </p>
              )}
              <div className="absolute inset-0 overflow-hidden">
                <div style={iframeWrapperStyle}>
                  <iframe
                    srcDoc={chatbotSrcDoc || undefined}
                    src={!chatbotSrcDoc ? chatbotFallbackSrc || undefined : undefined}
                    className="w-full h-full border-0"
                    title="AI Helpdesk Assistant"
                    allow="same-origin allow-scripts allow-forms allow-popups allow-modals"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    onError={() => {
                      console.error('Error loading chatbot');
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {isMinimized && (
            <div className="flex-1 bg-white flex items-center justify-between px-4">
              <div className="text-sm text-slate-600 truncate">Chatbot is minimized</div>
              <button
                onClick={handleExpand}
                className="text-sm font-medium text-[#0AAE94] hover:underline"
                aria-label="Expand chatbot"
              >
                Expand
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LandingChatbot;
